import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, CircularProgress, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, FormControlLabel, Checkbox, IconButton, Tooltip, DialogContentText, Snackbar, Alert, Chip, Switch, Grid } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import UploadIcon from '@mui/icons-material/Upload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import API_CONFIG from '../config/api';
import { apiFetch, getAuthenticatedApiUrl, getAuthenticatedUploadUrl, getToken } from '../api/client';
import RegulamentoModal from '../components/RegulamentoModal';
import PrestadorWizardDialog from '../components/prestadores/PrestadorWizardDialog';
import AditivoWizardDialog from '../components/prestadores/AditivoWizardDialog';
import ParcelasContratoDialog from '../components/prestadores/ParcelasContratoDialog';
import RateioConsolidadoDialog from '../components/prestadores/RateioConsolidadoDialog';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import {
  labelTipoContrato,
  labelCategoriaServico,
  chipColorTipoContrato,
  chipColorCategoriaServico,
  inferClassificacaoFromLegacy,
} from '../constants/prestadorClassificacao';
import html2pdf from 'html2pdf.js';

const BACKEND_URL = API_CONFIG.BACKEND_URL;

function resolverUrlUploadTermo(urlOrPath) {
  if (!urlOrPath) return null;
  const raw = String(urlOrPath);
  if (raw.includes('/uploads/')) {
    const idx = raw.indexOf('/uploads/');
    return getAuthenticatedUploadUrl(raw.slice(idx + '/uploads/'.length));
  }
  if (raw.startsWith('uploads/')) {
    return getAuthenticatedUploadUrl(raw.replace(/^uploads\//, ''));
  }
  return getAuthenticatedUploadUrl(raw);
}

function pathExt(name) {
  const m = String(name || '').toLowerCase().match(/\.[a-z0-9]+$/);
  return m ? m[0] : '';
}

function sanitizarNomeArquivoTermo(nome) {
  return String(nome || 'sem-nome')
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 140) || 'sem-nome';
}

/** Nome padrão: Designação de Fiscal - Empresa - 019/2025.ext */
function nomeArquivoDesignacaoFiscal(empresa, numeroContrato, ext) {
  const emp = sanitizarNomeArquivoTermo(empresa);
  const num = sanitizarNomeArquivoTermo(numeroContrato || '');
  const base = num
    ? `Designação de Fiscal - ${emp} - ${num}`
    : `Designação de Fiscal - ${emp}`;
  const e = String(ext || 'pdf').replace(/^\./, '');
  return `${base}.${e}`;
}

function baixarBlobLocal(blob, filename) {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}

async function converterPdfParaPngFile(file) {
  const pdfjs = await import('pdfjs-dist/build/pdf');
  try {
    // eslint-disable-next-line import/no-webpack-loader-syntax
    const worker = await import('pdfjs-dist/build/pdf.worker.entry');
    pdfjs.GlobalWorkerOptions.workerSrc = worker;
  } catch {
    pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
  }
  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjs.getDocument({ data }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 2 });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Não foi possível converter o PDF em imagem.');
  return new File([blob], 'moldura.png', { type: 'image/png' });
}

/** Abre ou baixa arquivo da API com token (evita erro ao abrir em nova aba). */
async function abrirArquivoComAuth(apiPath, download = false) {
  const token = getToken();
  try {
    const res = await fetch(`${BACKEND_URL}${apiPath}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Erro ao abrir arquivo. Faça login novamente.');
      return;
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    if (download) {
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : 'documento';
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      window.open(blobUrl, '_blank');
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch {
    alert('Erro ao abrir arquivo.');
  }
}

const unidadeQuery = (unidadeSelecionada) =>
  unidadeSelecionada?.codigo ? `?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}` : '';

const unidadeQueryAmp = (unidadeSelecionada) =>
  unidadeSelecionada?.codigo ? `&unidade=${encodeURIComponent(unidadeSelecionada.codigo)}` : '';

function normTxt(v) {
  return String(v || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function formatCpfDisplay(cpf) {
  const d = String(cpf || '').replace(/\D/g, '');
  if (d.length !== 11) return cpf || '___.___.___-__';
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function matchUsuarioPorNomeOuEmail(usuarios, nome, email) {
  const list = Array.isArray(usuarios) ? usuarios.filter((u) => !u.inativo) : [];
  const emailN = normTxt(email);
  if (emailN) {
    const byEmail = list.find((u) => normTxt(u.email) === emailN);
    if (byEmail) return byEmail;
  }
  const nomeN = normTxt(nome);
  if (!nomeN) return null;
  return (
    list.find((u) => normTxt(u.nome) === nomeN) ||
    list.find((u) => nomeN && normTxt(u.nome).includes(nomeN)) ||
    list.find((u) => nomeN && normTxt(u.nome) && nomeN.includes(normTxt(u.nome)))
  );
}

function usuariosDoSetor(usuarios, setor) {
  const setorN = normTxt(setor);
  if (!setorN) return [];
  return (Array.isArray(usuarios) ? usuarios : []).filter((u) => {
    if (u.inativo) return false;
    return normTxt(u.setor) === setorN;
  });
}

function escolherFiscalDoSetor(usuarios, setor, fiscalCadastro) {
  const list = usuariosDoSetor(usuarios, setor);
  if (fiscalCadastro) {
    const match = matchUsuarioPorNomeOuEmail(list.length ? list : usuarios, fiscalCadastro.nome, fiscalCadastro.email);
    if (match) return { ...fiscalCadastro, ...match, nome: fiscalCadastro.nome || match.nome };
  }
  if (!list.length) return fiscalCadastro || null;
  const prioridade = (u) => {
    const t = `${u.tipo || ''} ${u.cargo || ''}`.toLowerCase();
    if (t.includes('fiscal')) return 1;
    if (t.includes('coordenador') || t.includes('supervisor')) return 2;
    if (t.includes('analista') || t.includes('assistente')) return 3;
    return 4;
  };
  const escolhido = [...list].sort((a, b) => prioridade(a) - prioridade(b) || String(a.nome).localeCompare(String(b.nome)))[0];
  return escolhido;
}

function escolherSuplenteDoSetor(usuarios, setor, fiscalNome, fiscalEmail) {
  const list = usuariosDoSetor(usuarios, setor).filter((u) => {
    if (fiscalEmail && normTxt(u.email) === normTxt(fiscalEmail)) return false;
    if (fiscalNome && normTxt(u.nome) === normTxt(fiscalNome)) return false;
    return true;
  });
  if (!list.length) return null;
  const prioridade = (u) => {
    const t = `${u.tipo || ''} ${u.cargo || ''}`.toLowerCase();
    if (t.includes('diretor')) return 1;
    if (t.includes('gerente') || t.includes('coordenador') || t.includes('supervisor')) return 2;
    return 3;
  };
  return [...list].sort((a, b) => prioridade(a) - prioridade(b) || String(a.nome).localeCompare(String(b.nome)))[0];
}

function escolherDiretorExecutivo(usuarios = []) {
  const list = Array.isArray(usuarios) ? usuarios.filter((u) => !u.inativo) : [];
  const prioridade = (u) => {
    const t = `${u.tipo || ''} ${u.cargo || ''}`.toLowerCase();
    if (t.includes('diretor executivo')) return 1;
    if (t.includes('diretor')) return 2;
    return 9;
  };
  const candidatos = list.filter((u) => prioridade(u) < 9);
  if (!candidatos.length) return null;
  return [...candidatos].sort(
    (a, b) => prioridade(a) - prioridade(b) || String(a.nome || '').localeCompare(String(b.nome || ''))
  )[0];
}

async function enriquecerContratoTermo(prestador, fiscais, unidadeSelecionada, usuarios = []) {
  if (!prestador) return null;
  const setorContrato = prestador.setor;
  const fiscalCadastro = (fiscais || []).find(
    (f) => f.setor && setorContrato && normTxt(f.setor) === normTxt(setorContrato)
  );
  const fiscalUser = escolherFiscalDoSetor(usuarios, setorContrato, fiscalCadastro);
  const fiscalNome = fiscalUser?.nome || fiscalCadastro?.nome || null;
  const fiscalEmail = fiscalUser?.email || fiscalCadastro?.email || null;
  const fiscalId = fiscalUser?.id || null;

  // Preferir suplente da tab Substitutos (ATIVA, senão AGENDADA)
  let suplente = null;
  let suplenteAviso = null;
  try {
    const qs = new URLSearchParams();
    if (fiscalId) qs.set('fiscalId', String(fiscalId));
    else if (fiscalNome) qs.set('fiscalNome', fiscalNome);
    if (qs.toString()) {
      const resSub = await apiFetch(
        `/api/substituicoes/suplente-fiscal?${qs.toString()}`
      );
      if (resSub.ok) {
        const dataSub = await resSub.json();
        if (dataSub?.suplente) {
          suplente = dataSub.suplente;
          suplenteAviso = dataSub.aviso || null;
        }
      }
    }
  } catch {
    /* fallback abaixo */
  }
  if (!suplente) {
    suplente = escolherSuplenteDoSetor(usuarios, setorContrato, fiscalNome, fiscalEmail);
  }

  const diretor = escolherDiretorExecutivo(usuarios);

  const cargoFiscal =
    fiscalUser?.cargo ||
    fiscalCadastro?.cargo ||
    (fiscalUser?.tipo ? String(fiscalUser.tipo) : null) ||
    'Fiscal';

  return {
    ...prestador,
    processo: prestador.processo || prestador.numeroProcesso,
    unidadeGestora: unidadeSelecionada?.codigo || prestador.unidade || 'CEAP-SOL',
    fiscalNome,
    fiscalSetor: fiscalUser?.setor || fiscalCadastro?.setor || setorContrato,
    fiscalCargo: cargoFiscal,
    fiscalCpf: formatCpfDisplay(fiscalUser?.cpf || fiscalCadastro?.cpf),
    fiscalEmail,
    suplenteNome: suplente?.nome,
    suplenteCpf: formatCpfDisplay(suplente?.cpf),
    suplenteCargo: suplente?.cargo || suplente?.tipo || '___________________',
    suplenteSetor: suplente?.setor || setorContrato,
    diretorNome: diretor?.nome || null,
    suplenteAviso,
  };
}

function gerarTemplateTermo(contrato, molduraUrl = null) {
  const processo = contrato?.processo || '_______';
  const dataAtual = new Date().toLocaleDateString('pt-BR');
  const unidadeGestora = contrato?.unidadeGestora || 'CEAP-SOL';
  const blank = '________';
  const fiscalNome = contrato?.fiscalNome || blank;
  const fiscalCpf = contrato?.fiscalCpf || blank;
  const fiscalCargo = contrato?.fiscalCargo || blank;
  const fiscalSetor = contrato?.fiscalSetor || blank;
  const suplenteNome = contrato?.suplenteNome || blank;
  const suplenteCpf = contrato?.suplenteCpf || blank;
  const suplenteSetor = contrato?.suplenteSetor || blank;
  const diretorNome = contrato?.diretorNome || blank;
  const contratoNumero = contrato?.numeroContrato || blank;

  // Moldura = fundo da folha A4 inteira (logo + laterais), conteúdo por cima com margem
  const paginaStyle = molduraUrl
    ? [
        'font-family: Arial, sans-serif',
        'font-size: 14px',
        'line-height: 1.5',
        'text-align: justify',
        'color: #000',
        'width: 210mm',
        'min-height: 297mm',
        'margin: 0 auto',
        'box-sizing: border-box',
        'position: relative',
        'background-color: #ffffff',
        `background-image: url('${molduraUrl}')`,
        'background-repeat: repeat-y',
        'background-size: 210mm 297mm',
        'background-position: top center'
      ].join('; ')
    : [
        'font-family: Arial, sans-serif',
        'font-size: 14px',
        'line-height: 1.5',
        'text-align: justify',
        'color: #000',
        'width: 210mm',
        'min-height: 297mm',
        'margin: 0 auto',
        'box-sizing: border-box',
        'padding: 40px',
        'background: #ffffff'
      ].join('; ');

  const conteudoStyle = molduraUrl
    ? 'position: relative; z-index: 1; padding: 42mm 22mm 28mm 20mm; box-sizing: border-box;'
    : '';

  const placeholderSemMoldura = !molduraUrl
    ? `<div style="text-align: center; border-bottom: 2px solid #4CAF50; padding-bottom: 10px; margin-bottom: 20px;">
        <p style="margin: 0; font-weight: bold; color: #333;">[ Envie a moldura/timbrado (folha inteira) pelo botão acima ]</p>
      </div>`
    : '';

  const alinea = (letra, verbo, resto) =>
    `<p style="margin: 0 0 10px 18px; text-align: justify; page-break-inside: avoid;"><strong>${letra})</strong> <strong>${verbo}</strong> ${resto}</p>`;

  return `
  <div class="termo-pagina" data-tem-moldura="${molduraUrl ? '1' : '0'}" style="${paginaStyle}">
    <div class="termo-conteudo" style="${conteudoStyle}">
    ${placeholderSemMoldura}

    <h3 style="text-align: center; font-weight: bold;">ATO DE DESIGNAÇÃO – FISCAL DE CONTRATOS</h3>
    <br/>
    <h4 style="font-weight: bold; background-color: #f0f0f0; padding: 5px;">DADOS DO CONTRATO</h4>
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
      <tr>
        <td style="padding: 4px;"><strong>PROCESSO Nº:</strong> ${processo}</td>
        <td style="padding: 4px;"><strong>DATA:</strong> ${dataAtual}</td>
      </tr>
      <tr>
        <td style="padding: 4px;"><strong>CONTRATO Nº:</strong> ${contrato?.numeroContrato || '_______'}</td>
        <td style="padding: 4px;"><strong>UNIDADE GESTORA:</strong> ${unidadeGestora}</td>
      </tr>
      <tr>
        <td colspan="2" style="padding: 4px;"><strong>CONTRATADO:</strong> ${contrato?.empresa || '_______'}</td>
      </tr>
      <tr>
        <td style="padding: 4px;"><strong>CNPJ:</strong> ${contrato?.cnpj || '_______'}</td>
        <td style="padding: 4px;"><strong>VALOR DO CONTRATO:</strong> ${contrato?.valorMes || '_______'}</td>
      </tr>
      <tr>
        <td colspan="2" style="padding: 4px;"><strong>VIGÊNCIA:</strong> ${contrato?.vigenciaInicio || '_______'} A ${contrato?.vigenciaFim || '_______'}</td>
      </tr>
      <tr>
        <td colspan="2" style="padding: 4px;"><strong>OBJETO:</strong> ${contrato?.objeto || '________________________________________________'}</td>
      </tr>
    </table>

    <h4 style="font-weight: bold; text-align: center;">Designação do Fiscal do Contrato</h4>
    <p style="text-align: justify;">Dispõe sobre a designação de Fiscal para assistir e subsidiar o gestor do contrato indicado na epígrafe e dá outras providências.</p>
    <p style="text-align: justify;">O Diretor Executivo, no uso das atribuições, resolve:</p>
    <p style="text-align: justify;">Designar o(a) colaborador(a) <strong>${fiscalNome}</strong>, <strong>${fiscalCargo}</strong> inscrito(a) no CPF: <strong>${fiscalCpf}</strong>, lotado(a) no(a) Setor <strong>${fiscalSetor}</strong> como Fiscal do Contrato nº <strong>${contratoNumero}</strong> - que representará a unidade perante o contratado e zelará pela boa execução do objeto pactuado, exercendo as atividades de orientação, fiscalização e controle previstas no contrato, devendo ainda:</p>

    ${alinea('a', 'Anotar', 'de forma organizada, em registro próprio e em ordem cronológica, todas as ocorrências relacionadas com a execução do contrato;')}
    ${alinea('b', 'Conferir', 'o cumprimento do objeto e demais obrigações pactuadas, especialmente o atendimento às especificações atinentes ao objeto e sua garantia, bem como os prazos fixados no contrato, visitando o local onde o contrato esteja sendo executado e registrando os pontos críticos encontrados, inclusive com a produção de provas, datando, assinando e colhendo a assinatura do preposto da contratada para instruir possível procedimento de sanção contratual;')}
    ${alinea('c', 'Comunicar', 'ao Setor de Contratos sobre o descumprimento, pela contratada, de quaisquer das obrigações passíveis de rescisão contratual e/ou aplicação de penalidades a fim de formalizar notificação à CONTRATADA.')}
    ${alinea('d', 'Exigir', 'que a contratada substitua os produtos/bens que se apresentem defeituosos ou com prazo de validade vencido ou por vencer em curto prazo de tempo e que, por esses motivos, inviabilizem o recebimento definitivo, a guarda ou a utilização pelo contratante;')}
    ${alinea('e', 'Comunicar', 'imediatamente à contratada, quando o fornecimento seja de sua obrigação, a escassez de material cuja falta esteja dificultando a execução dos serviços;')}
    ${alinea('f', 'Recusar', 'os serviços executados em desacordo com o pactuado e determinar desfazimento, ajustes ou correções;')}
    ${alinea('g', 'Testar', 'o funcionamento de equipamentos e registrar a conformidade em documento;')}
    ${alinea('h', 'Analisar', 'conferir e atestar as notas fiscais;')}
    ${alinea('i', 'Analisar', 'conferir e validar a documentação mensal pertinente ao objeto do contrato para pagamento;')}
    ${alinea('j', 'Comunicar', 'ao setor de contratos eventual subcontratação da execução, sem previsão contratual ou sem conhecimento da Diretoria;')}
    ${alinea('k', 'Fiscalizar', 'pessoalmente, os registros dos empregados da contratada locados nos serviços, para verificar a regularidade trabalhista;')}
    ${alinea('l', 'Verificar', 'por intermédio do preposto da contratada, a utilização pelos empregados da empresa dos equipamentos de proteção individual exigidos pela legislação pertinente, exigindo daquele a interdição do acesso ao local de trabalho, e na hipótese de descumprimento, comunicar à Administração para promoção do possível processo punitivo contratual;')}
    ${alinea('m', 'Exigir', 'por intermédio do preposto da contratada, a utilização de crachá e de uniforme pelos empregados da contratada, quando for o caso, e conduta compatível com as normas da unidade, pautada pela ética e cuidado no atendimento;')}
    ${alinea('n', 'Cobrar', 'da contratada, quando se tratar de obras, no local de execução dos serviços, na formatação padrão combinada, o Diário de Obra, cujas folhas deverão estar devidamente numeradas e assinadas pelas partes, e onde serão feitas as anotações diárias sobre o andamento dos trabalhos tais como: indicação técnica, início e término de etapas de serviço, causas e datas de início e término de eventuais interrupções dos serviços, recebimento de material e demais assuntos que requeiram providências;')}
    ${alinea('o', 'Zelar', 'para que o contratado registre as ocorrências referidas no item anterior no Diário de Obra, com vista a compor o processo e servir como documento para dirimir dúvidas e embasar informações acerca de eventuais reivindicações futuras;')}

    <p style="text-align: justify; margin-top: 16px; page-break-inside: avoid;">Na ausência do servidor(a) supra designado, fica designado como suplente o(a) <strong>${suplenteNome}</strong>, inscrito no CPF: <strong>${suplenteCpf}</strong>, Lotado no (a) <strong>${suplenteSetor}</strong>.</p>

    <div style="text-align: center; margin-top: 48px; page-break-inside: avoid;">
      <p>_________________________________________________</p>
      <p><strong>${diretorNome}</strong></p>
      <p>Diretor Executivo</p>
    </div>

    <h4 style="font-weight: bold; text-align: center; margin-top: 40px; page-break-before: auto;">CIÊNCIA DOS COLABORADORES DESIGNADOS</h4>
    <div style="page-break-inside: avoid;">
      <p style="text-align: justify;">Eu, <strong>${fiscalNome}</strong>, declaro-me ciente da designação ora atribuída, e das funções que são inerentes em razão da função.</p>
      <div style="text-align: center; margin-top: 40px;">
        <p>_________________________________________________</p>
        <p><strong>Assinatura Fiscal</strong></p>
      </div>
    </div>

    <div style="margin-top: 30px; page-break-inside: avoid;">
      <p style="text-align: justify;">Eu, <strong>${suplenteNome}</strong>, declaro-me ciente da designação ora atribuída, e das funções que são inerentes em razão da função.</p>
      <div style="text-align: center; margin-top: 40px;">
        <p>_________________________________________________</p>
        <p><strong>Assinatura Suplente</strong></p>
      </div>
    </div>

    <div style="text-align: center; margin-top: 60px; border-top: 1px solid #ccc; padding-top: 10px; font-size: 11px; color: #666;">
      <p style="margin: 0;">Gerado pelo Sistema GAV-H - Setor de Contratos</p>
    </div>
    </div>
  </div>
`;
}

async function gerarPdfDoTermo(element, numeroContrato, empresa) {
  const filename = nomeArquivoDesignacaoFiscal(empresa, numeroContrato, 'pdf');
  const root = element?.querySelector?.('.termo-pagina') || element;
  const temMoldura =
    root?.getAttribute?.('data-tem-moldura') === '1' ||
    element?.querySelector?.('[data-tem-moldura="1"]');
  const opt = {
    margin: temMoldura ? [0, 0, 0, 0] : [10, 10, 10, 10],
    filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff'
    },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    pagebreak: { mode: ['css', 'legacy'] }
  };
  const blob = await html2pdf().set(opt).from(element).outputPdf('blob');
  return new File([blob], filename, { type: 'application/pdf' });
}

async function gerarWordDoTermo(htmlContent, numeroContrato, empresa) {
  const filename = nomeArquivoDesignacaoFiscal(empresa, numeroContrato, 'doc');
  const res = await apiFetch('/api/termos-designacao/html-para-docx', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ html: htmlContent, numeroContrato, empresa })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Erro ao gerar documento Word');
  }
  const blob = await res.blob();
  return new File([blob], filename, { type: 'application/msword' });
}

export default function PrestadoresPage() {
  const { isAdmin, isCargoCorporativo, canEditPrestadores, unidadeSelecionada, unidades, canSwitchUnidade } = useAuth();
  const podeRatear = Boolean(
    isAdmin || isCargoCorporativo || (typeof canSwitchUnidade === 'function' ? canSwitchUnidade() : canSwitchUnidade)
  );
  const [prestadores, setPrestadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('ativos'); // ativos | inativos | todos
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardPrestador, setWizardPrestador] = useState(null);
  const [aditivoWizardOpen, setAditivoWizardOpen] = useState(false);
  const [aditivoWizardEdit, setAditivoWizardEdit] = useState(null);
  const [aditivoWizardPrestadorRef, setAditivoWizardPrestadorRef] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [uploadingId, setUploadingId] = useState(null);
  
  // Estados para ordenação
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Modal de listagem de aditivos
  const [modalAditivos, setModalAditivos] = useState({ open: false, aditivos: [], prestador: null });
  const aditivoPdfInputRef = useRef(null);
  const [uploadingAditivoId, setUploadingAditivoId] = useState(null);
  const [parcelasModal, setParcelasModal] = useState({ open: false, prestador: null });
  const [rateioModal, setRateioModal] = useState({ open: false, prestador: null });

  // Estados para modal de regulamentos
  const [regulamentoModal, setRegulamentoModal] = useState({ open: false, regulamento: null, mode: 'add' });

  // Estados para modal de termos de designação
  const [termosModalOpen, setTermosModalOpen] = useState(false);
  const [termos, setTermos] = useState([]);
  const [termoForm, setTermoForm] = useState({
    numeroContrato: '',
    empresa: ''
  });
  const [conteudoTermo, setConteudoTermo] = useState('');
  const [setorSelecionado, setSetorSelecionado] = useState('');
  const [termosSelecionados, setTermosSelecionados] = useState([]);
  const [termosBulkDeleting, setTermosBulkDeleting] = useState(false);
  const [fiscaisTermo, setFiscaisTermo] = useState([]);
  const [usuariosTermo, setUsuariosTermo] = useState([]);
  const [molduraTermoUrl, setMolduraTermoUrl] = useState(null);
  const [molduraTermoNome, setMolduraTermoNome] = useState('');
  const [molduraTermoUploading, setMolduraTermoUploading] = useState(false);
  const [termoLoading, setTermoLoading] = useState(false);
  const [termoSalvandoModo, setTermoSalvandoModo] = useState(null);
  const [termoError, setTermoError] = useState('');
  const [termoAviso, setTermoAviso] = useState('');
  const [termoMaloteLoading, setTermoMaloteLoading] = useState(false);
  const [termoAssinadoUploading, setTermoAssinadoUploading] = useState(false);
  const termoEditorRef = useRef(null);
  const termoMolduraInputRef = useRef(null);
  const termoAssinadoInputRef = useRef(null);
  const termoMaloteInputRef = useRef(null);

  useEffect(() => {
    if (isAdmin) {
      fetchPrestadores();
    }
  }, [isAdmin, unidadeSelecionada]);

  // Listener para abrir modal de regulamentos
  useEffect(() => {
    const handleOpenRegulamentoModal = (event) => {
      setRegulamentoModal({
        open: true,
        regulamento: event.detail.regulamento || null,
        mode: event.detail.mode || 'add'
      });
    };

    window.addEventListener('openRegulamentoModal', handleOpenRegulamentoModal);
    
    return () => {
      window.removeEventListener('openRegulamentoModal', handleOpenRegulamentoModal);
    };
  }, []);

  useEffect(() => {
    if (!termosModalOpen) return;
    fetchTermos();
    fetchFiscaisTermo();
    fetchUsuariosTermo();
    fetchMolduraTermo();
  }, [termosModalOpen, unidadeSelecionada]);

  useEffect(() => {
    if (!termosModalOpen || !termoForm.numeroContrato) return;
    const prestador = prestadores.find((p) => p.numeroContrato === termoForm.numeroContrato);
    if (!prestador) return;
    let cancelled = false;
    (async () => {
      const contrato = await enriquecerContratoTermo(
        prestador,
        fiscaisTermo,
        unidadeSelecionada,
        usuariosTermo
      );
      if (cancelled || !contrato) return;
      setConteudoTermo(gerarTemplateTermo(contrato, molduraTermoUrl));
      if (contrato.suplenteAviso) {
        setTermoAviso(contrato.suplenteAviso);
      } else {
        setTermoAviso('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fiscaisTermo, usuariosTermo, molduraTermoUrl, termosModalOpen, termoForm.numeroContrato, prestadores, unidadeSelecionada]);

  const setoresTermo = useMemo(() => {
    const setores = prestadores
      .map((p) => p.setor)
      .filter((s) => s && String(s).trim())
      .map((s) => String(s).trim());
    return [...new Set(setores)].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [prestadores]);

  const prestadoresFiltradosTermo = useMemo(() => {
    if (!setorSelecionado) return prestadores;
    return prestadores.filter(
      (p) => p.setor && normTxt(p.setor) === normTxt(setorSelecionado)
    );
  }, [prestadores, setorSelecionado]);

  const fetchFiscaisTermo = async () => {
    try {
      const res = await apiFetch(`/api/fiscais${unidadeQuery(unidadeSelecionada)}`);
      if (res.ok) {
        setFiscaisTermo(await res.json());
      } else {
        setFiscaisTermo([]);
      }
    } catch {
      setFiscaisTermo([]);
    }
  };

  const fetchUsuariosTermo = async () => {
    try {
      const qs = unidadeSelecionada?.codigo
        ? `?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : '';
      const res = await apiFetch(`/api/usuarios${qs}`);
      if (res.ok) {
        const data = await res.json();
        setUsuariosTermo(Array.isArray(data) ? data : []);
      } else {
        setUsuariosTermo([]);
      }
    } catch {
      setUsuariosTermo([]);
    }
  };

  const fetchMolduraTermo = async () => {
    try {
      const res = await apiFetch('/api/termos-designacao/moldura');
      if (!res.ok) {
        setMolduraTermoUrl(null);
        return;
      }
      const data = await res.json();
      const url = resolverUrlUploadTermo(data.moldura_path || data.moldura_url);
      setMolduraTermoUrl(url);
      setMolduraTermoNome(data.moldura_path || data.original_path || '');
    } catch {
      setMolduraTermoUrl(null);
    }
  };

  const fecharModalTermos = () => {
    setTermosModalOpen(false);
    setTermoForm({ numeroContrato: '', empresa: '' });
    setConteudoTermo('');
    setSetorSelecionado('');
    setTermosSelecionados([]);
    setTermoError('');
    setTermoAviso('');
  };

  const handleSelecionarSetorTermo = (setor) => {
    setSetorSelecionado(setor);
    if (!termoForm.numeroContrato) return;

    const contratoValido = prestadores.some(
      (p) =>
        p.numeroContrato === termoForm.numeroContrato &&
        (!setor || (p.setor && normTxt(p.setor) === normTxt(setor)))
    );

    if (!contratoValido) {
      setTermoForm({ numeroContrato: '', empresa: '' });
      setConteudoTermo('');
    }
  };

  const handleSelecionarContratoTermo = async (numeroContrato) => {
    const prestador = prestadores.find((p) => p.numeroContrato === numeroContrato);
    if (!prestador) {
      setTermoForm({ numeroContrato: '', empresa: '' });
      setConteudoTermo('');
      return;
    }
    setTermoForm({
      numeroContrato,
      empresa: prestador.empresa || ''
    });
    const contrato = await enriquecerContratoTermo(
      prestador,
      fiscaisTermo,
      unidadeSelecionada,
      usuariosTermo
    );
    const html = gerarTemplateTermo(contrato, molduraTermoUrl);
    setConteudoTermo(html);
    if (contrato?.suplenteAviso) {
      setTermoAviso(contrato.suplenteAviso);
    } else {
      setTermoAviso('');
    }
  };

  const enviarArquivoMolduraTermo = async (file) => {
    const formData = new FormData();
    formData.append('moldura', file);
    const res = await apiFetch('/api/termos-designacao/moldura', {
      method: 'POST',
      body: formData
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao salvar moldura.');
    return data;
  };

  const handleMolduraTermoChange = async (event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = '';
    if (!file) return;

    setMolduraTermoUploading(true);
    setTermoError('');
    try {
      const ext = pathExt(file.name);
      const isImage = ['.png', '.jpg', '.jpeg'].includes(ext) || String(file.type).startsWith('image/');
      const isPdf = ext === '.pdf' || file.type === 'application/pdf';
      const isWord = ['.doc', '.docx'].includes(ext);

      if (isImage) {
        const data = await enviarArquivoMolduraTermo(file);
        const url = resolverUrlUploadTermo(data.moldura_path || data.moldura_url);
        setMolduraTermoUrl(url);
        setMolduraTermoNome(file.name);
        setSnackbar({ open: true, message: 'Moldura/timbrado salvo no cabeçalho.', severity: 'success' });
      } else if (isPdf) {
        await enviarArquivoMolduraTermo(file);
        const pngFile = await converterPdfParaPngFile(file);
        const data = await enviarArquivoMolduraTermo(pngFile);
        const url = resolverUrlUploadTermo(data.moldura_path || data.moldura_url);
        setMolduraTermoUrl(url);
        setMolduraTermoNome(file.name);
        setSnackbar({
          open: true,
          message: 'PDF convertido e aplicado no cabeçalho/timbrado.',
          severity: 'success'
        });
      } else if (isWord) {
        await enviarArquivoMolduraTermo(file);
        setMolduraTermoNome(file.name);
        setSnackbar({
          open: true,
          message:
            'Word da moldura salvo. Para aparecer no cabeçalho do PDF/Word gerado, envie também PNG/JPG ou PDF do timbrado.',
          severity: 'info'
        });
      } else {
        throw new Error('Formato inválido. Use PNG, JPG, PDF ou Word.');
      }
    } catch (error) {
      console.error('Erro moldura termo:', error);
      setTermoError(error.message || 'Erro ao enviar moldura.');
      setSnackbar({ open: true, message: error.message || 'Erro ao enviar moldura.', severity: 'error' });
    } finally {
      setMolduraTermoUploading(false);
    }
  };

  // Verificação de admin deve vir depois de todos os hooks
  if (!isAdmin) return <Navigate to="/" replace />;

  // Função para formatar CNPJ
  const formatCNPJ = (value) => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '');
    
    // Limita a 14 dígitos
    const limited = numbers.slice(0, 14);
    
    // Aplica a máscara
    if (limited.length <= 2) return limited;
    if (limited.length <= 5) return `${limited.slice(0, 2)}.${limited.slice(2)}`;
    if (limited.length <= 8) return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5)}`;
    if (limited.length <= 12) return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8)}`;
    return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8, 12)}-${limited.slice(12)}`;
  };

  // Função para formatar valor em R$
  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'R$ 0,00';
    return numValue.toLocaleString('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    });
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Se a data está no formato DD/MM/YYYY, retorna como está
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      return dateString; // Já está no formato correto
    }
    
    // Se a data está no formato YYYY-MM-DD, converte para DD/MM/YYYY
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Fallback para o comportamento original
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('pt-BR');
  };

  const fetchPrestadores = async () => {
    setLoading(true);
    try {
      // Adicionar filtro de unidade na URL
      const url = unidadeSelecionada 
        ? `${BACKEND_URL}/api/prestadores?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/prestadores`;
      
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erro ao buscar prestadores');
      const data = await res.json();

      setPrestadores(data);
    } catch {
      setPrestadores([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Se clicar na mesma coluna, inverte a direção
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Se clicar em uma nova coluna, define como ascendente
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortPrestadores = (prestadores) => {
    if (!sortField) return prestadores;

    return [...prestadores].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Tratamento especial para valores numéricos
      if (sortField === 'valorMes') {
        aValue = parseFloat(aValue?.replace(/[R$\s.]/g, '').replace(',', '.')) || 0;
        bValue = parseFloat(bValue?.replace(/[R$\s.]/g, '').replace(',', '.')) || 0;
      }

      // Tratamento para valores de data
      if (sortField === 'vigenciaInicio' || sortField === 'vigenciaFim' || sortField === 'assinatura') {
        aValue = new Date(aValue || '1900-01-01');
        bValue = new Date(bValue || '1900-01-01');
      }

      // Tratamento para valores booleanos
      if (sortField === 'eventual' || sortField === 'corporativo') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      }

      // Comparação
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredPrestadores = sortPrestadores(
    prestadores.filter((prestador) => {
      const inativo = Boolean(prestador.finalizado);
      if (filtroStatus === 'ativos' && inativo) return false;
      if (filtroStatus === 'inativos' && !inativo) return false;
      // Evita listar cópia rateada na mesma unidade da matriz (mesmo nº + fornecedor)
      if (
        (prestador.eh_rateio_filho === 1 || prestador.eh_rateio_filho === true) &&
        prestadores.some(
          (p) =>
            p.id !== prestador.id &&
            String(p.numeroContrato || '').trim() === String(prestador.numeroContrato || '').trim() &&
            String(p.cnpj || '').replace(/\D/g, '') === String(prestador.cnpj || '').replace(/\D/g, '') &&
            (p.eh_contrato_matriz === 1 || p.eh_contrato_matriz === true) &&
            !p.eh_rateio_filho
        )
      ) {
        return false;
      }
      const q = search.toLowerCase();
      return (
        prestador.numeroContrato?.toLowerCase().includes(q) ||
        prestador.empresa?.toLowerCase().includes(q) ||
        prestador.cnpj?.toLowerCase().includes(q)
      );
    })
  );

  // Componente para cabeçalho da tabela com ordenação
  const SortableTableHeader = ({ field, label, onClick }) => {
    const isActive = sortField === field;
    const isAsc = sortDirection === 'asc';
    
    return (
      <TableCell 
        onClick={onClick}
        sx={{ 
          cursor: 'pointer', 
          userSelect: 'none',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          whiteSpace: 'nowrap'
        }}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          {label}
          {isActive && (
            isAsc ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
          )}
        </Box>
      </TableCell>
    );
  };

  const handleOpenModal = (prestador = null) => {
    setWizardPrestador(prestador || null);
    setWizardOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await apiFetch(`/api/prestadores/${deleteId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Erro ao excluir prestador');
      setDeleteDialogOpen(false);
      setDeleteId(null);
      fetchPrestadores();
    } catch {
      // Pode exibir erro se quiser
    }
  };

  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const res = await apiFetch(`/api/prestadores/export-excel${unidadeQuery(unidadeSelecionada)}`);
      if (!res.ok) throw new Error('Erro ao exportar Excel');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prestadores_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbar({ open: true, message: 'Exportação realizada com sucesso!', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Erro ao exportar Excel.', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await apiFetch('/api/prestadores/import-excel', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Erro ao importar Excel');
      setSnackbar({ open: true, message: 'Importação realizada com sucesso!', severity: 'success' });
      fetchPrestadores();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao importar Excel.', severity: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const handleUploadPDF = (id) => {
    pdfInputRef.current.click();
    setUploadingId(id);
  };

  const handlePDFChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadingId) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await apiFetch(`/api/prestadores/${uploadingId}/upload-contract`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) throw new Error('Erro ao fazer upload do PDF');
      
      setSnackbar({
        open: true,
        message: 'PDF enviado com sucesso!',
        severity: 'success'
      });
      
      fetchPrestadores();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao fazer upload do PDF.',
        severity: 'error'
      });
    } finally {
      setUploadingId(null);
      e.target.value = '';
    }
  };

  const handleViewPDF = (prestador) => {
    if (!prestador.pdfPath && !prestador.contratoPath) {
      setSnackbar({
        open: true,
        message: 'Nenhum PDF anexado para este prestador.',
        severity: 'warning'
      });
      return;
    }
    
    const pdfPath = prestador.pdfPath || prestador.contratoPath;
    window.open(getAuthenticatedUploadUrl(pdfPath), '_blank');
  };

  const handleOpenAditivoModal = () => {
    setAditivoWizardEdit(null);
    setAditivoWizardPrestadorRef(null);
    setAditivoWizardOpen(true);
  };

  // Função para abrir modal de aditivos de um prestador
  const handleOpenAditivosModal = async (prestador) => {
    try {
      const resAditivos = await apiFetch(
        `/api/aditivos?numeroContrato=${encodeURIComponent(prestador.numeroContrato)}${unidadeQueryAmp(unidadeSelecionada)}`
      );
      if (!resAditivos.ok) throw new Error('Erro ao buscar aditivos');
      const aditivos = await resAditivos.json();
      if (!Array.isArray(aditivos) || aditivos.length === 0) {
        setSnackbar({
          open: true,
          message: 'Nenhum aditivo encontrado para este contrato.',
          severity: 'warning'
        });
        return;
      }
      setModalAditivos({ open: true, aditivos, prestador });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Erro ao buscar aditivos',
        severity: 'error'
      });
    }
  };

  const handleEditarAditivo = (aditivo) => {
    setAditivoWizardEdit(aditivo);
    setAditivoWizardPrestadorRef(modalAditivos.prestador);
    setModalAditivos({ open: false, aditivos: [], prestador: null });
    setAditivoWizardOpen(true);
  };

  // Função para excluir aditivo
  const handleExcluirAditivo = (aditivo) => {
    if (window.confirm(`Tem certeza que deseja excluir o ${aditivo.numeroAditivo}° Termo Aditivo?`)) {
      apiFetch(`/api/aditivos/${aditivo.id}`, {
        method: 'DELETE',
      })
      .then(res => {
        if (!res.ok) throw new Error('Erro ao excluir aditivo');
        
        // Atualizar lista de aditivos na modal
        const aditivosAtualizados = modalAditivos.aditivos.filter(a => a.id !== aditivo.id);
        setModalAditivos(prev => ({ ...prev, aditivos: aditivosAtualizados }));
        
        setSnackbar({
          open: true,
          message: 'Aditivo excluído com sucesso!',
          severity: 'success'
        });
      })
      .catch(err => {
        setSnackbar({
          open: true,
          message: err.message || 'Erro ao excluir aditivo',
          severity: 'error'
        });
      });
    }
  };

  // Função para upload de PDF de aditivo
  const handleUploadAditivoPDF = (aditivoId) => {
    aditivoPdfInputRef.current.click();
    setUploadingAditivoId(aditivoId);
  };

  const handleAditivoPDFChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadingAditivoId) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await apiFetch(`/api/aditivos/${uploadingAditivoId}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) throw new Error('Erro ao fazer upload do PDF');

      setSnackbar({
        open: true,
        message: 'PDF do aditivo enviado com sucesso!',
        severity: 'success'
      });

      // Recarregar aditivos
      const prestador = modalAditivos.prestador;
      const resAditivos = await apiFetch(
        `/api/aditivos?numeroContrato=${encodeURIComponent(prestador.numeroContrato)}${unidadeQueryAmp(unidadeSelecionada)}`
      );
      const aditivos = await resAditivos.json();
      setModalAditivos({ ...modalAditivos, aditivos });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao fazer upload do PDF.',
        severity: 'error'
      });
    } finally {
      setUploadingAditivoId(null);
      e.target.value = '';
    }
  };

  // Função para ativar/desativar contrato
  const handleToggleFinalizado = async (prestadorId, finalizado) => {
    try {
      const res = await apiFetch(`/api/prestadores/${prestadorId}/finalizar`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ finalizado: finalizado ? 1 : 0 })
      });
      
      if (!res.ok) throw new Error('Erro ao atualizar status do contrato');
      
      await fetchPrestadores();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar status do contrato.',
        severity: 'error'
      });
    }
  };

  // Funções para gerenciar termos de designação
  const fetchTermos = async () => {
    try {
      console.log('Buscando termos de designação...', `${BACKEND_URL}/api/termos-designacao`);
      const res = await apiFetch(`/api/termos-designacao${unidadeQuery(unidadeSelecionada)}`);
      console.log('Resposta recebida:', res.status, res.statusText);
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Erro na resposta:', errorText);
        throw new Error(`Erro ${res.status}: ${errorText}`);
      }
      
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        console.error('Resposta não é JSON:', text);
        throw new Error('Resposta do servidor não é JSON válido');
      }
      
      const data = await res.json();
      console.log('Dados recebidos:', data);
      setTermos(data);
    } catch (error) {
      console.error('Erro ao buscar termos:', error);
      setTermos([]);
      setTermoError(`Erro ao carregar termos: ${error.message}`);
    }
  };

  const handleSalvarTermo = async (modo = 'ambos') => {
    setTermoError('');
    if (!termoForm.numeroContrato || !termoForm.empresa) {
      setTermoError('Selecione um contrato e preencha a empresa.');
      return;
    }
    if (!termoEditorRef.current || !conteudoTermo.trim()) {
      setTermoError('Gere o termo selecionando um contrato antes de salvar.');
      return;
    }

    const htmlAtual = termoEditorRef.current.innerHTML || conteudoTermo;

    setTermoLoading(true);
    setTermoSalvandoModo(modo);
    try {
      let pdfFile = null;
      let docxFile = null;

      if (modo === 'pdf' || modo === 'ambos') {
        pdfFile = await gerarPdfDoTermo(
          termoEditorRef.current,
          termoForm.numeroContrato,
          termoForm.empresa
        );
        baixarBlobLocal(pdfFile, pdfFile.name);
      }
      if (modo === 'word' || modo === 'ambos') {
        docxFile = await gerarWordDoTermo(htmlAtual, termoForm.numeroContrato, termoForm.empresa);
        baixarBlobLocal(docxFile, docxFile.name);
      }

      const formData = new FormData();
      formData.append('numeroContrato', termoForm.numeroContrato);
      formData.append('empresa', termoForm.empresa);
      if (pdfFile) formData.append('pdfFile', pdfFile);
      if (docxFile) formData.append('docxFile', docxFile);

      const res = await apiFetch('/api/termos-designacao', {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Erro ao salvar termo');
      }

      const msgPorModo = {
        pdf: 'PDF baixado e salvo no sistema.',
        word: 'Word baixado e salvo no sistema.',
        ambos: 'PDF e Word baixados e salvos no sistema.'
      };

      setSnackbar({
        open: true,
        message: msgPorModo[modo] || 'Termo de designação adicionado com sucesso!',
        severity: 'success'
      });

      setTermoForm({ numeroContrato: '', empresa: '' });
      setConteudoTermo('');
      if (termoEditorRef.current) termoEditorRef.current.innerHTML = '';
      fetchTermos();
    } catch (error) {
      console.error('Erro ao salvar termo:', error);
      setTermoError(error.message || 'Erro ao gerar ou salvar o documento do termo.');
    } finally {
      setTermoLoading(false);
      setTermoSalvandoModo(null);
    }
  };

  const handleSubmeterAssinado = async (event, termoAlvo = null) => {
    const file = event?.target?.files?.[0];
    if (event?.target) event.target.value = '';
    if (!file) return;

    const numeroContrato = termoAlvo?.numero_contrato || termoForm.numeroContrato;
    const empresa = termoAlvo?.empresa || termoForm.empresa;
    if (!numeroContrato || !empresa) {
      setTermoError('Selecione um contrato antes de submeter o PDF assinado.');
      return;
    }

    setTermoAssinadoUploading(true);
    setTermoError('');
    try {
      const formData = new FormData();
      formData.append('numeroContrato', numeroContrato);
      formData.append('empresa', empresa);
      formData.append('pdfFile', file, nomeArquivoDesignacaoFiscal(empresa, numeroContrato, 'pdf'));

      const res = await apiFetch('/api/termos-designacao/submeter-assinado', {
        method: 'POST',
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao submeter PDF assinado.');

      setSnackbar({
        open: true,
        message: `PDF assinado salvo para ${empresa}. Disponível em Fiscais de Contratos.`,
        severity: 'success'
      });
      fetchTermos();
    } catch (error) {
      console.error('Erro ao submeter assinado:', error);
      setTermoError(error.message || 'Erro ao submeter PDF assinado.');
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao submeter PDF assinado.',
        severity: 'error'
      });
    } finally {
      setTermoAssinadoUploading(false);
    }
  };

  const handleBaixarMaloteTermos = async () => {
    setTermoMaloteLoading(true);
    setTermoError('');
    try {
      const qs = setorSelecionado
        ? `?setor=${encodeURIComponent(setorSelecionado)}`
        : '';
      const res = await apiFetch(`/api/termos-designacao/malote${qs}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Erro ao baixar malote.');
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const filename = match
        ? decodeURIComponent(match[1].replace(/"/g, ''))
        : `Malote - Designação de Fiscais.zip`;
      baixarBlobLocal(blob, filename);
      setSnackbar({
        open: true,
        message: setorSelecionado
          ? `Malote baixado (setor ${setorSelecionado}).`
          : 'Malote com todas as designações baixado.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro malote download:', error);
      setTermoError(error.message || 'Erro ao baixar malote.');
      setSnackbar({ open: true, message: error.message || 'Erro ao baixar malote.', severity: 'error' });
    } finally {
      setTermoMaloteLoading(false);
    }
  };

  const handleSubmeterMaloteTermos = async (event) => {
    const file = event?.target?.files?.[0];
    if (event?.target) event.target.value = '';
    if (!file) return;

    setTermoMaloteLoading(true);
    setTermoError('');
    try {
      const formData = new FormData();
      formData.append('malote', file);
      const res = await apiFetch('/api/termos-designacao/malote', {
        method: 'POST',
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao processar malote.');

      const ok = (data.ok || []).length;
      const falhas =
        (data.nao_encontrado || []).length +
        (data.ambiguo || []).length +
        (data.erros || []).length;
      setSnackbar({
        open: true,
        message: `Malote processado: ${ok} salvo(s)${falhas ? `, ${falhas} pendente(s)` : ''}.`,
        severity: falhas && !ok ? 'warning' : 'success'
      });
      if (falhas) {
        const detalhes = [
          ...(data.nao_encontrado || []).map((i) => `${i.arquivo}: não encontrado`),
          ...(data.ambiguo || []).map((i) => `${i.arquivo}: ambíguo`),
          ...(data.erros || []).map((i) => `${i.arquivo}: ${i.erro}`)
        ].slice(0, 8);
        setTermoError(detalhes.join(' | '));
      }
      fetchTermos();
    } catch (error) {
      console.error('Erro malote upload:', error);
      setTermoError(error.message || 'Erro ao submeter malote.');
      setSnackbar({ open: true, message: error.message || 'Erro ao submeter malote.', severity: 'error' });
    } finally {
      setTermoMaloteLoading(false);
    }
  };

  const handleDeletarTermo = async (termoId, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm('Tem certeza que deseja excluir este termo de designação?')) {
      return false;
    }

    try {
      const res = await apiFetch(`/api/termos-designacao/${termoId}`, {
        method: 'DELETE'
      });

      if (!res.ok) {
        throw new Error('Erro ao excluir termo');
      }

      if (!skipConfirm) {
        setSnackbar({
          open: true,
          message: 'Termo de designação excluído com sucesso!',
          severity: 'success'
        });
        fetchTermos();
      }

      return true;
    } catch (error) {
      console.error('Erro ao excluir termo:', error);
      if (!skipConfirm) {
        setSnackbar({
          open: true,
          message: 'Erro ao excluir termo de designação.',
          severity: 'error'
        });
      }
      return false;
    }
  };

  const handleToggleTermoSelecionado = (termoId) => {
    setTermosSelecionados((prev) =>
      prev.includes(termoId) ? prev.filter((id) => id !== termoId) : [...prev, termoId]
    );
  };

  const handleToggleTodosTermos = (checked) => {
    setTermosSelecionados(checked ? termos.map((t) => t.id) : []);
  };

  const handleExcluirTermosSelecionados = async () => {
    if (termosSelecionados.length === 0) return;

    const quantidade = termosSelecionados.length;
    if (
      !window.confirm(
        `Tem certeza que deseja excluir ${quantidade} termo(s) de designação selecionado(s)?`
      )
    ) {
      return;
    }

    setTermosBulkDeleting(true);
    try {
      const resultados = await Promise.all(
        termosSelecionados.map((id) => handleDeletarTermo(id, true))
      );
      const excluidos = resultados.filter(Boolean).length;
      const falhas = quantidade - excluidos;

      setSnackbar({
        open: true,
        message:
          falhas > 0
            ? `${excluidos} termo(s) excluído(s). ${falhas} falha(s).`
            : `${excluidos} termo(s) excluído(s) com sucesso!`,
        severity: falhas > 0 ? 'warning' : 'success'
      });

      setTermosSelecionados([]);
      fetchTermos();
    } finally {
      setTermosBulkDeleting(false);
    }
  };

  const todosTermosSelecionados = termos.length > 0 && termosSelecionados.length === termos.length;
  const algunsTermosSelecionados =
    termosSelecionados.length > 0 && termosSelecionados.length < termos.length;

  const handleEditarTermo = async (termo, novoPdfFile) => {
    try {
      const formData = new FormData();
      formData.append('numeroContrato', termo.numero_contrato);
      formData.append('empresa', termo.empresa);
      if (novoPdfFile) {
        formData.append('pdfFile', novoPdfFile);
      }

      const res = await apiFetch(`/api/termos-designacao/${termo.id}`, {
        method: 'PUT',
        body: formData
      });

      if (!res.ok) {
        throw new Error('Erro ao editar termo');
      }

      setSnackbar({
        open: true,
        message: 'Termo de designação atualizado com sucesso!',
        severity: 'success'
      });

      fetchTermos();
    } catch (error) {
      console.error('Erro ao editar termo:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao editar termo de designação.',
        severity: 'error'
      });
    }
  };

  return (
    <>
      <input
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleImportExcel}
      />
      <input
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        ref={pdfInputRef}
        onChange={handlePDFChange}
      />
      <input
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        ref={aditivoPdfInputRef}
        onChange={handleAditivoPDFChange}
      />
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700}>Prestadores</Typography>
                                    <Box display="flex" gap={1}>
                          <Button variant="contained" color="primary" onClick={() => handleOpenModal()}>Novo Prestador</Button>
                          <Button variant="contained" color="secondary" onClick={handleOpenAditivoModal}>Novo Aditivo</Button>
                          <Button
                            variant="outlined"
                            color="success"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportExcel}
                            disabled={exporting}
                          >
                            {exporting ? 'Exportando...' : 'Exportar Excel'}
                          </Button>
                          <Button
                            variant="outlined"
                            color="info"
                            startIcon={<UploadFileIcon />}
                            onClick={handleImportClick}
                            disabled={importing}
                          >
                            {importing ? 'Importando...' : 'Importar Excel'}
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<DescriptionIcon />}
                            onClick={() => window.dispatchEvent(new CustomEvent('openRegulamentoModal', { detail: { mode: 'add' } }))}
                            sx={{
                              background: 'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(66,165,245,0.1) 100%)',
                              border: '1px solid rgba(25,118,210,0.3)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, rgba(25,118,210,0.2) 0%, rgba(66,165,245,0.2) 100%)',
                                border: '1px solid rgba(25,118,210,0.5)'
                              }
                            }}
                          >
                            Gerenciar Regulamentos
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setTermosModalOpen(true)}
                            sx={{
                              borderRadius: 2,
                              px: 3,
                              py: 1.5,
                              transition: 'all 0.3s ease',
                              background: 'linear-gradient(135deg, rgba(76,175,80,0.1) 0%, rgba(139,195,74,0.1) 100%)',
                              border: '1px solid rgba(76,175,80,0.3)',
                              color: '#4CAF50',
                              '&:hover': {
                                background: 'linear-gradient(135deg, rgba(76,175,80,0.2) 0%, rgba(139,195,74,0.2) 100%)',
                                border: '1px solid rgba(76,175,80,0.5)'
                              }
                            }}
                          >
                            Adicionar termos
                          </Button>
                        </Box>
          </Box>
          <TextField
            label="Buscar por Nº Contrato, Empresa ou CNPJ"
            variant="outlined"
            size="small"
            fullWidth
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
            <TextField
              select
              size="small"
              label="Status"
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="ativos">Ativos</MenuItem>
              <MenuItem value="inativos">Inativos</MenuItem>
              <MenuItem value="todos">Todos</MenuItem>
            </TextField>
            <Typography variant="caption" color="text.secondary">
              Excluir (lixeira) inativa o contrato; use o filtro para ver inativos.
            </Typography>
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableHeader 
                      field="numeroContrato" 
                      label="Nº Contrato" 
                      onClick={() => handleSort('numeroContrato')} 
                    />
                    <SortableTableHeader 
                      field="empresa" 
                      label="Empresa" 
                      onClick={() => handleSort('empresa')} 
                    />
                    <SortableTableHeader 
                      field="cnpj" 
                      label="CNPJ" 
                      onClick={() => handleSort('cnpj')} 
                    />
                    <SortableTableHeader 
                      field="setor" 
                      label="Setor" 
                      onClick={() => handleSort('setor')} 
                    />
                    <SortableTableHeader 
                      field="valorMes" 
                      label="Valor/Mês" 
                      onClick={() => handleSort('valorMes')} 
                    />
                    <SortableTableHeader 
                      field="vigenciaInicio" 
                      label="Início" 
                      onClick={() => handleSort('vigenciaInicio')} 
                    />
                    <SortableTableHeader 
                      field="vigenciaFim" 
                      label="Fim" 
                      onClick={() => handleSort('vigenciaFim')} 
                    />
                    <TableCell>Parcelas</TableCell>
                    <TableCell>Status</TableCell>
                    {!isCargoCorporativo && <TableCell align="center">Ações</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPrestadores.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isCargoCorporativo ? 9 : 10} align="center">Nenhum prestador encontrado</TableCell>
                    </TableRow>
                  ) : (
                    filteredPrestadores.map((prestador) => (
                      <TableRow key={prestador.id}>
                        <TableCell>{prestador.numeroContrato}</TableCell>
                        <TableCell>{prestador.empresa}</TableCell>
                        <TableCell>{formatCNPJ(prestador.cnpj || '')}</TableCell>
                        <TableCell>{prestador.setor}</TableCell>
                        <TableCell>{formatCurrency(prestador.valorMes || '')}</TableCell>
                        <TableCell>{formatDate(prestador.vigenciaInicio)}</TableCell>
                        <TableCell>{formatDate(prestador.vigenciaFim)}</TableCell>
                        <TableCell
                          sx={{ cursor: 'pointer' }}
                          onClick={() => setParcelasModal({ open: true, prestador })}
                        >
                          {(() => {
                            const r = prestador.parcelasResumo;
                            if (!r || !r.total) {
                              return (
                                <Typography variant="caption" color="text.secondary">
                                  —
                                </Typography>
                              );
                            }
                            const atrasada =
                              r.proximoVencimento &&
                              String(r.proximoVencimento).slice(0, 10) < new Date().toISOString().slice(0, 10) &&
                              r.pagas < r.total;
                            return (
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {r.pagas}/{r.total} pagas
                                </Typography>
                                {r.proximoVencimento && r.pagas < r.total && (
                                  <Typography
                                    variant="caption"
                                    color={atrasada ? 'error.main' : 'text.secondary'}
                                  >
                                    Próx.: {formatDate(r.proximoVencimento)}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={prestador.finalizado ? 'Inativo' : 'Ativo'} 
                            color={prestador.finalizado ? 'default' : 'success'} 
                            size="small" 
                          />
                          {(() => {
                            const { tipoContrato, categoriaServico } = inferClassificacaoFromLegacy(prestador);
                            return (
                              <>
                                <Chip
                                  label={labelTipoContrato(tipoContrato)}
                                  color={chipColorTipoContrato(tipoContrato)}
                                  size="small"
                                  sx={{ ml: 0.5 }}
                                />
                                <Chip
                                  label={labelCategoriaServico(categoriaServico)}
                                  color={chipColorCategoriaServico(categoriaServico)}
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: 0.5 }}
                                />
                                {(prestador.corporativo || prestador.corporativo === 1 || prestador.corporativo === true) && (
                                  <Chip
                                    label="Corporativo"
                                    color="info"
                                    size="small"
                                    sx={{ ml: 0.5 }}
                                  />
                                )}
                                {(prestador.eh_rateio_filho === 1 || prestador.eh_rateio_filho === true) && (
                                  <Chip
                                    label="Rateado — Matriz"
                                    color="secondary"
                                    size="small"
                                    sx={{ ml: 0.5 }}
                                  />
                                )}
                                {(prestador.eh_contrato_matriz === 1 || prestador.eh_contrato_matriz === true) && (
                                  <Chip
                                    label="Matriz (rateado)"
                                    color="primary"
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 0.5 }}
                                  />
                                )}
                              </>
                            );
                          })()}
                        </TableCell>
                        {!isCargoCorporativo && (
                          <TableCell align="center">
                            <Tooltip title="Upload PDF">
                              <IconButton 
                                color="default" 
                                onClick={() => handleUploadPDF(prestador.id)} 
                                size="small"
                                disabled={uploadingId === prestador.id}
                              >
                                <PictureAsPdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                                                        <Tooltip title="Visualizar PDF">
                              <span>
                                <IconButton
                                  color="secondary"
                                  onClick={() => handleViewPDF(prestador)}
                                  size="small"
                                  disabled={!prestador.pdfPath && !prestador.contratoPath}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Parcelas / pagamentos">
                              <IconButton
                                color="primary"
                                onClick={() => setParcelasModal({ open: true, prestador })}
                                size="small"
                              >
                                <ScheduleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(prestador.eh_contrato_matriz === 1 || prestador.eh_contrato_matriz === true) && (
                              <Tooltip title="Consolidado do rateio">
                                <IconButton
                                  color="secondary"
                                  onClick={() => setRateioModal({ open: true, prestador })}
                                  size="small"
                                >
                                  <AccountTreeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Gerenciar Aditivos">
                              <IconButton
                                color="info"
                                onClick={() => handleOpenAditivosModal(prestador)}
                                size="small"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton color="primary" onClick={() => handleOpenModal(prestador)} size="small">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                              <IconButton color="error" onClick={() => openDeleteDialog(prestador.id)} size="small">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={prestador.finalizado ? "Ativar Contrato" : "Desativar Contrato"}>
                              <Switch
                                checked={Boolean(prestador.finalizado)}
                                onChange={(e) => handleToggleFinalizado(prestador.id, e.target.checked)}
                                size="small"
                                color="primary"
                              />
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <PrestadorWizardDialog
        open={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          setWizardPrestador(null);
        }}
        prestador={wizardPrestador}
        unidadeSelecionada={unidadeSelecionada}
        podeRatear={podeRatear}
        unidades={unidades || []}
        onSaved={(result) => {
          fetchPrestadores();
          setWizardOpen(false);
          setWizardPrestador(null);
          setSnackbar({
            open: true,
            severity: 'success',
            message: result?.portal?.senhaInicial
              ? `${result.edit ? 'Prestador atualizado' : 'Prestador cadastrado'}. Senha portal: ${result.portal.senhaInicial}`
              : result?.edit
                ? 'Prestador atualizado com sucesso.'
                : 'Prestador cadastrado com sucesso.'
          });
        }}
      />

      <ParcelasContratoDialog
        open={parcelasModal.open}
        prestador={parcelasModal.prestador}
        unidadeSelecionada={unidadeSelecionada}
        onClose={() => setParcelasModal({ open: false, prestador: null })}
        onUpdated={() => fetchPrestadores()}
      />

      <RateioConsolidadoDialog
        open={rateioModal.open}
        prestador={rateioModal.prestador}
        unidadeSelecionada={unidadeSelecionada}
        onClose={() => setRateioModal({ open: false, prestador: null })}
      />

      <AditivoWizardDialog
        open={aditivoWizardOpen}
        onClose={() => {
          setAditivoWizardOpen(false);
          setAditivoWizardEdit(null);
          setAditivoWizardPrestadorRef(null);
        }}
        unidadeSelecionada={unidadeSelecionada}
        aditivoEdit={aditivoWizardEdit}
        prestadorRef={aditivoWizardPrestadorRef}
        podeRatear={podeRatear}
        unidades={unidades || []}
        onSaved={({ edit }) => {
          fetchPrestadores();
          setSnackbar({
            open: true,
            severity: 'success',
            message: edit ? 'Aditivo atualizado com sucesso!' : 'Aditivo cadastrado com sucesso!'
          });
        }}
      />

      {/* Dialog de confirmação de exclusão */}
      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Excluir Prestador</DialogTitle>
        <DialogContent>
          <DialogContentText>Tem certeza que deseja excluir este prestador?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="secondary">Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aditivos */}
      <Dialog open={modalAditivos.open} onClose={() => setModalAditivos({ open: false, aditivos: [], prestador: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          Aditivos - Contrato {modalAditivos.prestador?.numeroContrato}
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nº Aditivo</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell>Valor/Mês</TableCell>
                  <TableCell>Vigência</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalAditivos.aditivos.map((aditivo) => (
                  <TableRow key={aditivo.id}>
                    <TableCell>{aditivo.numeroAditivo}</TableCell>
                    <TableCell>{aditivo.empresa}</TableCell>
                    <TableCell>{aditivo.valorMes}</TableCell>
                    <TableCell>{aditivo.vigenciaInicio} a {aditivo.vigenciaFim}</TableCell>
                    <TableCell>
                      <Tooltip title="Upload PDF">
                        <IconButton
                          color="default"
                          onClick={() => handleUploadAditivoPDF(aditivo.id)}
                          size="small"
                          disabled={uploadingAditivoId === aditivo.id}
                        >
                          <PictureAsPdfIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Visualizar PDF">
                        <span>
                          <IconButton
                            color="secondary"
                            onClick={() => {
                              if (aditivo.pdfPath) {
                                window.open(getAuthenticatedUploadUrl(aditivo.pdfPath), '_blank');
                              } else {
                                setSnackbar({
                                  open: true,
                                  message: 'Nenhum PDF anexado para este aditivo.',
                                  severity: 'warning'
                                });
                              }
                            }}
                            size="small"
                            disabled={!aditivo.pdfPath}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar Aditivo">
                        <IconButton
                          color="primary"
                          onClick={() => handleEditarAditivo(aditivo)}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir Aditivo">
                        <IconButton
                          color="error"
                          onClick={() => handleExcluirAditivo(aditivo)}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalAditivos({ open: false, aditivos: [], prestador: null })} color="secondary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Regulamentos */}
      <RegulamentoModal
        open={regulamentoModal.open}
        onClose={() => setRegulamentoModal({ open: false, regulamento: null, mode: 'add' })}
        regulamento={regulamentoModal.regulamento}
        mode={regulamentoModal.mode}
      />

      {/* Modal de Termos de Designação */}
      <Dialog open={termosModalOpen} onClose={fecharModalTermos} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          Gerenciar Termos de Designação
        </DialogTitle>
        <DialogContent sx={{ p: 3, maxHeight: '85vh', overflow: 'auto' }}>
          {/* Formulário para adicionar novo termo */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#4CAF50' }}>
              Adicionar Novo Termo
            </Typography>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap={2} alignItems="center">
              <TextField
                select
                label="Setor"
                value={setorSelecionado}
                onChange={(e) => handleSelecionarSetorTermo(e.target.value)}
                fullWidth
              >
                <MenuItem value="">Todos os setores</MenuItem>
                {setoresTermo.map((setor) => (
                  <MenuItem key={setor} value={setor}>
                    {setor}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Contrato"
                value={termoForm.numeroContrato}
                onChange={(e) => handleSelecionarContratoTermo(e.target.value)}
                fullWidth
                required
              >
                <MenuItem value="">Selecione um contrato</MenuItem>
                {prestadoresFiltradosTermo.map((prestador) => (
                  <MenuItem key={prestador.id} value={prestador.numeroContrato}>
                    {prestador.numeroContrato} - {prestador.empresa}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Empresa"
                value={termoForm.empresa}
                fullWidth
                required
                disabled
              />
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                <input
                  ref={termoMolduraInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,image/png,image/jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  hidden
                  onChange={handleMolduraTermoChange}
                />
                <Button
                  variant="outlined"
                  startIcon={
                    molduraTermoUploading ? <CircularProgress size={18} /> : <UploadFileIcon />
                  }
                  disabled={molduraTermoUploading}
                  onClick={() => termoMolduraInputRef.current?.click()}
                  sx={{ borderColor: '#6a1b9a', color: '#6a1b9a' }}
                >
                  {molduraTermoUploading ? 'Enviando moldura...' : 'Adicionar moldura / timbrado'}
                </Button>
                {molduraTermoUrl && (
                  <Typography variant="caption" color="text.secondary">
                    Timbrado ativo{molduraTermoNome ? `: ${molduraTermoNome}` : ''}
                  </Typography>
                )}
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
              <input
                ref={termoAssinadoInputRef}
                type="file"
                accept="application/pdf,.pdf"
                hidden
                onChange={(e) => handleSubmeterAssinado(e, null)}
              />
              <Button
                variant="outlined"
                onClick={() => termoAssinadoInputRef.current?.click()}
                disabled={termoAssinadoUploading || !termoForm.numeroContrato}
                startIcon={
                  termoAssinadoUploading ? <CircularProgress size={18} /> : <UploadFileIcon />
                }
                sx={{ borderColor: '#ef6c00', color: '#ef6c00' }}
              >
                {termoAssinadoUploading ? 'Enviando...' : 'Submeter assinado'}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleSalvarTermo('pdf')}
                disabled={termoLoading || !termoForm.numeroContrato}
                startIcon={
                  termoLoading && termoSalvandoModo === 'pdf'
                    ? <CircularProgress size={18} color="inherit" />
                    : <PictureAsPdfIcon />
                }
                sx={{
                  backgroundColor: '#d32f2f',
                  '&:hover': { backgroundColor: '#b71c1c' }
                }}
              >
                {termoLoading && termoSalvandoModo === 'pdf' ? 'Gerando PDF...' : 'Salvar PDF'}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleSalvarTermo('word')}
                disabled={termoLoading || !termoForm.numeroContrato}
                startIcon={
                  termoLoading && termoSalvandoModo === 'word'
                    ? <CircularProgress size={18} color="inherit" />
                    : <DescriptionIcon />
                }
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': { backgroundColor: '#1565c0' }
                }}
              >
                {termoLoading && termoSalvandoModo === 'word' ? 'Gerando Word...' : 'Salvar Word'}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleSalvarTermo('ambos')}
                disabled={termoLoading || !termoForm.numeroContrato}
                startIcon={
                  termoLoading && termoSalvandoModo === 'ambos'
                    ? <CircularProgress size={18} color="inherit" />
                    : <DownloadIcon />
                }
                sx={{
                  backgroundColor: '#4CAF50',
                  '&:hover': { backgroundColor: '#388E3C' }
                }}
              >
                {termoLoading && termoSalvandoModo === 'ambos' ? 'Salvando...' : 'Salvar PDF + Word'}
              </Button>
              </Box>
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: '#555' }}>
              Pré-visualização do Ato de Designação (editável antes de salvar)
            </Typography>
            <Box
              sx={{
                maxHeight: '55vh',
                overflow: 'auto',
                border: '1px solid #bdbdbd',
                borderRadius: 1,
                bgcolor: '#e8e8e8',
                p: 2
              }}
            >
              {conteudoTermo ? (
                <div
                  ref={termoEditorRef}
                  key={`${termoForm.numeroContrato}|${molduraTermoUrl || 'no-moldura'}|${usuariosTermo.length}|${fiscaisTermo.length}`}
                  contentEditable
                  suppressContentEditableWarning
                  dangerouslySetInnerHTML={{ __html: conteudoTermo }}
                  onBlur={(e) => setConteudoTermo(e.currentTarget.innerHTML)}
                  style={{
                    background: 'transparent',
                    color: '#000',
                    minHeight: '297mm',
                    width: '210mm',
                    maxWidth: '100%',
                    margin: '0 auto',
                    padding: 0,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                    outline: 'none',
                    overflow: 'hidden'
                  }}
                />
              ) : (
                <Box
                  sx={{
                    background: '#fff',
                    color: '#666',
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    p: 4
                  }}
                >
                  <Typography variant="body2">
                    Selecione um contrato acima para gerar automaticamente o Ato de Designação de Fiscal.
                  </Typography>
                </Box>
              )}
            </Box>

            {termoAviso && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {termoAviso}
              </Alert>
            )}
            {termoError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {termoError}
              </Alert>
            )}
          </Box>

          {/* Lista de termos existentes */}
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
            <Typography variant="h6" sx={{ color: '#4CAF50' }}>
              Termos Cadastrados
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <input
                ref={termoMaloteInputRef}
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                hidden
                onChange={handleSubmeterMaloteTermos}
              />
              <Button
                variant="outlined"
                startIcon={termoMaloteLoading ? <CircularProgress size={18} /> : <DownloadIcon />}
                onClick={handleBaixarMaloteTermos}
                disabled={termoMaloteLoading || termos.length === 0}
              >
                {termoMaloteLoading ? 'Processando...' : 'Baixar malote'}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<UploadFileIcon />}
                onClick={() => termoMaloteInputRef.current?.click()}
                disabled={termoMaloteLoading}
              >
                Submeter malote
              </Button>
              {termosSelecionados.length > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={termosBulkDeleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
                  onClick={handleExcluirTermosSelecionados}
                  disabled={termosBulkDeleting}
                >
                  {termosBulkDeleting
                    ? 'Excluindo...'
                    : `Excluir Selecionados (${termosSelecionados.length})`}
                </Button>
              )}
            </Box>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={algunsTermosSelecionados}
                      checked={todosTermosSelecionados}
                      onChange={(e) => handleToggleTodosTermos(e.target.checked)}
                      disabled={termos.length === 0}
                      inputProps={{ 'aria-label': 'Selecionar todos os termos' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Contrato</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Empresa</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Documentos</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Data Upload</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {termos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Nenhum termo de designação cadastrado
                    </TableCell>
                  </TableRow>
                ) : (
                  termos.map((termo) => (
                    <TableRow
                      key={termo.id}
                      selected={termosSelecionados.includes(termo.id)}
                      hover
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={termosSelecionados.includes(termo.id)}
                          onChange={() => handleToggleTermoSelecionado(termo.id)}
                          inputProps={{ 'aria-label': `Selecionar termo ${termo.numero_contrato}` }}
                        />
                      </TableCell>
                      <TableCell>{termo.numero_contrato}</TableCell>
                      <TableCell>{termo.empresa}</TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
                          {termo.pdf_path ? (
                            <Button
                              size="small"
                              startIcon={<PictureAsPdfIcon />}
                              onClick={() => abrirArquivoComAuth(`/api/termos-designacao/${termo.id}/pdf`, true)}
                              sx={{ color: '#d32f2f' }}
                            >
                              PDF
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Sem PDF</Typography>
                          )}
                          {termo.docx_path ? (
                            <Button
                              size="small"
                              startIcon={<DescriptionIcon />}
                              onClick={() => abrirArquivoComAuth(`/api/termos-designacao/${termo.id}/docx`, true)}
                              sx={{ color: '#1976d2' }}
                            >
                              Word
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Sem Word</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {Number(termo.assinado) === 1 ? (
                          <Chip label="Assinado" size="small" color="success" />
                        ) : (
                          <Chip label="Pendente" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{new Date(termo.data_upload).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Submeter PDF assinado">
                            <label htmlFor={`assinado-termo-${termo.id}`}>
                              <input
                                type="file"
                                accept="application/pdf,.pdf"
                                style={{ display: 'none' }}
                                id={`assinado-termo-${termo.id}`}
                                onChange={(e) => handleSubmeterAssinado(e, termo)}
                              />
                              <IconButton
                                size="small"
                                component="span"
                                sx={{ color: '#ef6c00' }}
                                disabled={termoAssinadoUploading}
                              >
                                <UploadFileIcon fontSize="small" />
                              </IconButton>
                            </label>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => handleDeletarTermo(termo.id)}
                            sx={{ color: '#f44336' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={fecharModalTermos} 
            variant="outlined"
            sx={{ 
              borderColor: '#4CAF50',
              color: '#4CAF50',
              '&:hover': { borderColor: '#388E3C', backgroundColor: 'rgba(76,175,80,0.1)' }
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
} 
